import React, { useState, useEffect, useMemo, useRef } from 'react'
import BrandConfigurationReportTab from '../brandConfigurationTab';
import BrandERPReportHeader from './erpReportHeader';
import { capitalizeAllFirstLetters, capitalizeFirstLetter, erpErrorText, formateDate, getCompany } from '../../../helpers/commonFunctions';
import CustomPagination from '../../../components/company_panel/common/custompagination';
import Loader from '../../../components/company_panel/common/loader';
import notFound from '../../../assets/company/img/no-record-found.png'
import CustomRangepicker from '../../../components/company_panel/common/rangepicker';
import { LOADER_TIME, SOCKET_EVENTS } from '../../../utils/Constants';
import { SOCKET_CLIENT } from '../../../utils/socket';
import { ListBrandERPPaymentReport, ExportBrandERPPaymentReport } from '../../../services/company_panel/brandReport.services';
import { GetBrandOutletsList } from '../../../services/company_panel/outletService';
import { GetBrandsHavingOutlets } from '../../../services/company_panel/brandService';
import { TagPicker, Tooltip, Whisper } from 'rsuite';
import Swal from 'sweetalert2';

const BrandERPPaymentsList = () => {

    const [list, setList] = useState([])
    const [dataLength, setDataLength] = useState()
    const [itemPerPage, setItemPerPage] = useState(10)
    const [page, setPage] = useState(1);
    const [loader, setLoader] = useState(true)
    const [search, setSearch] = useState({})
    const [globalSearch, setGlobalSearch] = useState("")
    const [resetdate, setResetDate] = useState(false);
    const [isBrandChanged, setIsBrandChanged] = useState(false);
    const [isOutletChanged, setIsOutletChanged] = useState(false);
    const [businessDateFilterValue, setBusinessDateFilterValue] = useState([]);

    const [brandList, setBrandList] = useState([]);
    const [outletList, setOutletList] = useState([]);
    const [selectedBrandIds, setSelectedBrandIds] = useState([]);
    const [selectedOutletIds, setSelectedOutletIds] = useState([]);

    // server-side export progress; completed/failed download+toast handled globally in companyLayout
    const [exportProgress, setExportProgress] = useState(null);
    const [exporting, setExporting] = useState(false);
    const exportJobIdRef = useRef(null);

    useMemo(() => {
        document.addEventListener('brand-changed', function () {
            setIsBrandChanged(prev => !prev);
        });
        document.addEventListener('outlet-changed', function () {
            setIsOutletChanged(prev => !prev);
        })
    }, []);

    useEffect(() => {
        setLoader(true)
        const formData = new FormData()
        formData.append("page", page)
        formData.append("per_page", itemPerPage)
        formData.append("search", JSON.stringify(search))
        formData.append("global_search", globalSearch)
        formData.append('company_id', getCompany().company_id);
        ListBrandERPPaymentReport(formData).then(data => {
            const response = data && data.data && data.data.response;
            setDataLength(response ? response.count : 0);
            setList(response ? response.payments_line_list : []);
            setTimeout(() => { setLoader(false) }, LOADER_TIME);
        }).catch(error => {
            console.log("error ====> ", error);
            setList([]);
            setDataLength(0);
            setTimeout(() => { setLoader(false) }, LOADER_TIME);
            Swal.fire({
                customClass: 'swal-wide',
                position: 'top-right',
                icon: 'error',
                title: error?.response?.data?.errors?.message || error?.message,
                showConfirmButton: false,
                timer: 1500,
                toast: true,
            })
        })
    }, [search, globalSearch, page, itemPerPage, isBrandChanged, isOutletChanged])

    useEffect(() => {
        GetBrandsHavingOutlets(getCompany().company_id).then(data => {
            setBrandList(data && data.data && data.data.response ? data.data.response : []);
        }).catch(error => {
            console.log("error=====>", error)
        })
    }, [])

    // only the PROGRESS bar is rendered here; completed / failed are handled globally
    useEffect(() => {
        const handleExportProgress = (response) => {
            const data = response?.data;
            if (!data || !data.jobId) return;
            if (response?.event !== SOCKET_EVENTS.ERP.PAYMENTS.EXPORT_PROGRESS) return;
            if (exportJobIdRef.current && data.jobId !== exportJobIdRef.current) return;

            const percent = Number(data.percent) || 0;
            setExportProgress({ status: percent >= 100 ? 'completed' : 'progress', percent, processed: data.processed, total: data.total });
            if (percent >= 100) {
                setExporting(false);
                exportJobIdRef.current = null;
                setTimeout(() => setExportProgress(null), 3000);
            }
        };
        SOCKET_CLIENT.on('GET-DATA', handleExportProgress);
        return () => { SOCKET_CLIENT.off('GET-DATA', handleExportProgress); };
    }, []);


    const prepareSearch = (key, value) => {
        let sr = { ...search };
        if (Array.isArray(value)) {
            if (value.length > 0) { sr[key] = value; } else { delete sr[key]; }
        } else if (String(value).length > 0) {
            sr[key] = value;
        } else {
            delete sr[key];
        }
        setSearch(sr);
        setPage(1);
    }

    const handleBrandChange = (selectedBrands) => {
        const brands = selectedBrands || [];
        setSelectedBrandIds(brands);

        if (brands.length === 0) {
            setOutletList([]);
            setSelectedOutletIds([]);
            const sr = { ...search };
            delete sr.brand_ids;
            delete sr.outlet_ids;
            setSearch(sr);
            return;
        }

        GetBrandOutletsList({ brand_ids: JSON.stringify(brands) }).then(data => {
            const outlets = data && data.data && data.data.response ? data.data.response : [];
            setOutletList(outlets);
            const validOutletIds = outlets.map(o => o.id);
            const filteredSelectedOutlets = selectedOutletIds.filter(id => validOutletIds.includes(id));
            setSelectedOutletIds(filteredSelectedOutlets);

            const sr = { ...search };
            sr.brand_ids = brands;
            if (filteredSelectedOutlets.length > 0) {
                sr.outlet_ids = filteredSelectedOutlets;
            } else {
                delete sr.outlet_ids;
            }
            setSearch(sr);
        }).catch(error => {
            console.log("error=====>", error)
        });
    }

    const handleOutletChange = (selectedOutlets) => {
        const outlets = selectedOutlets || [];
        setSelectedOutletIds(outlets);
        prepareSearch('outlet_ids', outlets);
    }

    const removeBrand = (brandId) => handleBrandChange(selectedBrandIds.filter(id => id !== brandId));
    const removeOutlet = (outletId) => handleOutletChange(selectedOutletIds.filter(id => id !== outletId));

    const brandData = brandList?.map(item => ({
        label: capitalizeAllFirstLetters(item?.name),
        value: item?.id
    })) || [];

    const filteredOutletData = outletList?.map(item => ({
        label: `${capitalizeAllFirstLetters(item?.outlet_name)}${item?.short_code ? ` (${item.short_code})` : ''}`,
        value: item?.id
    })) || [];

    const handleExport = () => {
        setExporting(true);
        setExportProgress({ status: 'progress', percent: 0 });
        const formData = new FormData()
        formData.append("search", JSON.stringify(search))
        formData.append("global_search", globalSearch)
        formData.append('company_id', getCompany().company_id);
        ExportBrandERPPaymentReport(formData).then(data => {
            const body = data?.data?.response || data?.data || {};
            const jobId = body.jobId || body.job_id || data?.data?.jobId;
            const message = data?.data?.message || body.message || 'Export started';
            if (jobId) exportJobIdRef.current = jobId;
            Swal.fire({ customClass: 'swal-wide', icon: 'success', title: message, showConfirmButton: false, timer: 3000, toast: true, position: 'top-right' });
        }).catch(error => {
            console.log("error ====> ", error);
            Swal.fire({ customClass: 'swal-wide', icon: 'error', title: error?.response?.data?.errors?.message || error?.message || 'Export failed', showConfirmButton: false, timer: 3000, toast: true, position: 'top-right' });
            setExporting(false);
            setExportProgress(null);
            exportJobIdRef.current = null;
        });
    }

    const resetFilter = (e) => {
        e.preventDefault()
        setGlobalSearch("")
        setSelectedBrandIds([]);
        setSelectedOutletIds([]);
        setOutletList([]);
        setSearch({})
        setResetDate(!resetdate)
        setPage(1);
    }

    return (
        <>
            <div className="animation_fade">
                <div className='page-header d-flex align-items-center'>
                    <div className='page-header-left'>
                        <h4>Brand Report</h4>
                    </div>
                    <div className='page-header-right ms-auto'>
                        <button onClick={handleExport} disabled={dataLength === 0 || exporting} className="btn btn-primary ms-2">{exporting ? 'Exporting...' : 'Export Report'}</button>
                    </div>
                </div>
                {exportProgress && (() => {
                    const isDone = exportProgress.status === 'completed';
                    const percent = exportProgress.percent || 0;
                    const accent = isDone ? '#22c55e' : '#2f9bff';
                    return (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '16px',
                            background: '#fff', border: '1px solid #e7ecf3', borderLeft: `4px solid ${accent}`,
                            borderRadius: '12px', padding: '14px 18px', marginBottom: '16px',
                            boxShadow: '0 2px 10px rgba(16, 30, 54, 0.06)'
                        }}>
                            <div style={{
                                width: '40px', height: '40px', flexShrink: 0, borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: isDone ? 'rgba(34,197,94,0.12)' : 'rgba(47,155,255,0.12)', color: accent
                            }}>
                                {isDone ? (
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                                ) : (
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'spin 0.9s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                                )}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600, color: '#1f2d3d', fontSize: '14px' }}>
                                        {isDone ? 'Export completed' : 'Exporting ERP payments report'}
                                        {exportProgress.total ? (
                                            <span style={{ fontWeight: 400, color: '#8a97a8', marginLeft: '8px', fontSize: '13px' }}>
                                                {(exportProgress.processed || 0).toLocaleString()} / {Number(exportProgress.total).toLocaleString()} records
                                            </span>
                                        ) : null}
                                    </span>
                                    <span style={{ fontWeight: 700, color: accent, fontSize: '14px', whiteSpace: 'nowrap' }}>{percent}%</span>
                                </div>
                                <div style={{ height: '8px', background: '#eef2f7', borderRadius: '999px', overflow: 'hidden' }}>
                                    <div style={{
                                        height: '100%', width: `${percent}%`, borderRadius: '999px',
                                        background: isDone ? accent : 'linear-gradient(90deg, #2f9bff, #6cb8ff)',
                                        transition: 'width 0.4s ease'
                                    }} />
                                </div>
                            </div>
                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        </div>
                    );
                })()}
                <div className="card custom-card overflow-hidden">
                    <div className='gap-20 border-bottom'>
                        <div className='col-12'>
                            <div className='d-flex justify-content-between align-items-center'>
                                <BrandConfigurationReportTab tab='erp' />
                            </div>
                        </div>
                    </div>
                    <div className="gap-20">
                        <div className='page-header d-flex align-items-center mb-0 flex-wrap'>
                            <div className='d-flex gap-3 align-items-center'>
                                <div className="form-group mb-0 rangepicker_container">
                                    <input type="search" className="form-control" value={globalSearch} placeholder="Search" onChange={(e) => { setGlobalSearch(e.target.value); setPage(1); }} />
                                </div>
                                <div className="form-group mb-0 filter_icon_container filter_design">
                                    <select
                                        className="form-control cp"
                                        value={search?.erp_filter || ''}
                                        onChange={(e) => prepareSearch("erp_filter", e.target.value)}
                                        style={{ width: "180px" }}
                                    >
                                        <option value="">Status Filter</option>
                                        <option value="uploaded">Uploaded</option>
                                        <option value="not_uploaded">Not Uploaded</option>
                                    </select>
                                </div>
                            </div>
                            <div className='page-header-right ms-auto'>
                                <div className='d-flex gap-3 align-items-center flex-wrap'>
                                    <div className="form-group mb-0 multi-filter-picker" style={{ width: '200px' }}>
                                        <TagPicker
                                            data={brandData}
                                            value={selectedBrandIds}
                                            onChange={handleBrandChange}
                                            placeholder="Select Brands"
                                            className='form-control form-control-select'
                                            cleanable={false}
                                            menuStyle={{ maxHeight: 200, overflowY: 'auto' }}
                                            renderValue={(value, items) => {
                                                const count = items.length;
                                                if (count === 0) return null;
                                                if (count === 1) {
                                                    return <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{items[0]?.label}</span>;
                                                }
                                                return <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>{count} brands selected</span>;
                                            }}
                                        />
                                    </div>
                                    <div className="form-group mb-0 multi-filter-picker" style={{ width: '200px' }}>
                                        <TagPicker
                                            data={filteredOutletData}
                                            value={selectedOutletIds}
                                            onChange={handleOutletChange}
                                            placeholder="Select Outlets"
                                            className='form-control form-control-select'
                                            cleanable={false}
                                            menuStyle={{ maxHeight: 200, overflowY: 'auto' }}
                                            renderValue={(value, items) => {
                                                const count = items.length;
                                                if (count === 0) return null;
                                                if (count === 1) {
                                                    return <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{items[0]?.label}</span>;
                                                }
                                                return <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>{count} outlets selected</span>;
                                            }}
                                        />
                                    </div>
                                    <div className="date-input">
                                        <CustomRangepicker business_date={true} value={businessDateFilterValue} GetDateRange={(e) => { prepareSearch("business_date", e) }} resetdate={resetdate} />
                                    </div>
                                    <button type="reset" value='Reset' onClick={resetFilter} className="btn btn-primary mr-2">Reset Filter</button>
                                </div>
                            </div>
                        </div>
                        {selectedBrandIds.length > 0 && (
                            <div className='d-flex flex-wrap gap-2 mt-2'>
                                <span className='text-muted me-1 align-self-center'>Brands:</span>
                                {selectedBrandIds.map(id => {
                                    const brand = brandList.find(b => b.id === id);
                                    if (!brand) return null;
                                    return (
                                        <span key={`b-${id}`} style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#2f9bff', color: '#fff', borderRadius: '14px', padding: '4px 10px', fontSize: '13px', fontWeight: 500 }}>
                                            <span>{capitalizeAllFirstLetters(brand.name)}</span>
                                            <button type='button' aria-label='Remove' onClick={() => removeBrand(id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', marginLeft: 6, padding: 0, lineHeight: 1, color: '#fff', display: 'inline-flex', alignItems: 'center' }}>
                                                <svg width='12' height='12' viewBox='0 0 16 16' fill='currentColor' aria-hidden='true'><path d='M2.784 2.089l.069.058 5.146 5.147 5.146-5.147a.5.5 0 01.765.638l-.058.069L8.705 8l5.147 5.146a.5.5 0 01-.638.765l-.069-.058-5.146-5.147-5.146 5.147a.5.5 0 01-.765-.638l.058-.069L7.293 8 2.146 2.854a.5.5 0 01.638-.765z'></path></svg>
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                        {selectedOutletIds.length > 0 && (
                            <div className='d-flex flex-wrap gap-2 mt-2'>
                                <span className='text-muted me-1 align-self-center'>Outlets:</span>
                                {selectedOutletIds.map(id => {
                                    const outlet = outletList.find(o => o.id === id);
                                    if (!outlet) return null;
                                    return (
                                        <span key={`o-${id}`} style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#e7f1ff', color: '#1565c0', border: '1px solid #b6d4fe', borderRadius: '14px', padding: '4px 10px', fontSize: '13px', fontWeight: 500 }}>
                                            <span>{capitalizeAllFirstLetters(outlet.outlet_name)}{outlet.short_code ? ` (${outlet.short_code})` : ''}</span>
                                            <button type='button' aria-label='Remove' onClick={() => removeOutlet(id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', marginLeft: 6, padding: 0, lineHeight: 1, color: '#1565c0', display: 'inline-flex', alignItems: 'center' }}>
                                                <svg width='12' height='12' viewBox='0 0 16 16' fill='currentColor' aria-hidden='true'><path d='M2.784 2.089l.069.058 5.146 5.147 5.146-5.147a.5.5 0 01.765.638l-.058.069L8.705 8l5.147 5.146a.5.5 0 01-.638.765l-.069-.058-5.146-5.147-5.146 5.147a.5.5 0 01-.765-.638l.058-.069L7.293 8 2.146 2.854a.5.5 0 01.638-.765z'></path></svg>
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <div className='gap-20 ps-0 pb-0 pe-0'>
                        <BrandERPReportHeader tab='payments' />
                    </div>
                    <div className="tabler-wrap">
                        <div className="table-responsive">
                            <table className="table table-custom">
                                <thead>
                                    <tr>
                                        <th width="12%">Business Date</th>
                                        <th width="12%">Brand</th>
                                        <th width="12%">Outlet</th>
                                        <th width="14%">Journal Batch Number</th>
                                        <th width="12%">Payment Method</th>
                                        <th width="10%">Total Price</th>
                                        <th width="18%">Description</th>
                                        <th width="10%">Status</th>
                                        <th width="8%">Error</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loader ? (
                                        <tr>
                                            <td colSpan={9}><Loader /></td>
                                        </tr>
                                    ) : (
                                        <>
                                            {list?.length ? list.map((payment, index) => (
                                                <tr key={index}>
                                                    <td>{formateDate(payment?.business_date)}</td>
                                                    <td>{payment?.brand?.name ? capitalizeFirstLetter(payment.brand.name) : '-'}</td>
                                                    <td>{payment?.outlet?.outlet_name ? `${capitalizeFirstLetter(payment.outlet.outlet_name)}${payment.outlet.short_code ? ` (${payment.outlet.short_code})` : ''}` : '-'}</td>
                                                    <td>{payment?.erp_details?.journal_batch_number || '-'}</td>
                                                    <td>{capitalizeFirstLetter(payment?.payment_method)}</td>
                                                    <td>{Number(payment?.total_amount).toFixed(2) + ' SAR'}</td>
                                                    <td>{payment?.description}</td>
                                                    <td>
                                                        {(payment?.erp_details?.error || !payment?.erp_details)
                                                            ? <span className="text-danger">Not Uploaded</span>
                                                            : <span className="text-success">Uploaded</span>}
                                                    </td>
                                                    {payment?.erp_details?.error ? (
                                                        <td>
                                                            <Whisper
                                                                placement="top"
                                                                controlId="control-id-hover"
                                                                trigger="click"
                                                                container={document.body}
                                                                flip={false}
                                                                preventOverflow
                                                                speaker={
                                                                    <Tooltip style={{ maxHeight: '200px', overflowY: 'auto', whiteSpace: 'normal' }}>
                                                                        Unable to create free text payment header. {erpErrorText(payment?.erp_details?.error)}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <span className="field-more-info ms-1 cp">
                                                                    <i className="fas fa-exclamation-circle text-danger"></i>
                                                                </span>
                                                            </Whisper>
                                                        </td>
                                                    ) : (
                                                        <td>-</td>
                                                    )}
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan={9} className="text-center">
                                                        <img src={notFound} style={{ width: '5%' }} alt="Not Found" />
                                                        <p>No record found</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="tabler-footer">
                            <div className="d-flex align-items-center">
                                <div id="example1_info" role="status" className="table-result" aria-live="polite">
                                    <b>Total Records: {dataLength ? dataLength : '0'}</b>
                                </div>
                                {dataLength && dataLength > 0 ? (
                                    <>
                                        <div className='ms-3'>
                                            Show
                                            <select
                                                value={itemPerPage}
                                                onChange={(e) => { setItemPerPage(Number(e.target.value)); setPage(1); }}
                                                className="mx-2"
                                            >
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                            entries
                                        </div>
                                        <CustomPagination
                                            datalength={dataLength}
                                            itemperpage={itemPerPage}
                                            setPage={setPage}
                                            currentPage={page}
                                            pageRoute={[{ name: 'brand-erp-payments', path: '/company/brand-report/erp_payments/list' }]}
                                        />
                                    </>
                                ) : ''}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default BrandERPPaymentsList
